import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Table, Tag, Select,
    Alert, Button, Progress, Statistic, Spin
} from 'antd';
import {
    WarningOutlined, CheckCircleOutlined,
    ClockCircleOutlined, CalendarOutlined,
    ReloadOutlined, FilePdfOutlined
} from '@ant-design/icons';
import QoCService from '../../services/QoCService';
import FacilityService from '../../services/FacilityService';
import { usePeriods } from '../../hooks/usePeriods';
import { FacilityReportModal } from './QoCFacilityReport';
import moment from 'moment';
import { trailPageView, trailReportGenerate, TRAIL_MODULES } from '../../utils/activityTrail';
const { Option } = Select;

const CLAUSE_COLORS = {
    AACC:'#0ea5e9', COP:'#8b5cf6', MOM:'#f59e0b', PRPE:'#10b981',
    IPCM:'#ef4444', PSQI:'#3b82f6', GROM:'#6366f1', FMS:'#ec4899',
    HRE:'#14b8a6',  ISM:'#a855f7',
};

const RATING = [
    { min:98,  max:100,  label:'Outstanding',          color:'#389e0d' },
    { min:95,  max:97.9, label:'Excellent',            color:'#1890ff' },
    { min:90,  max:94.9, label:'Good',                 color:'#52c41a' },
    { min:85,  max:89.9, label:'Fair',                 color:'#faad14' },
    { min:70,  max:84.9, label:'Requires Improvement', color:'#fa8c16' },
    { min:0,   max:69.9, label:'Inadequate',           color:'#f5222d' },
];
const getRating = s => RATING.find(r => s >= r.min && s <= r.max) || RATING[RATING.length-1];

export default function QoCCompliance() {
    const { periods, currentPeriod } = usePeriods();
    const [loading, setLoading]           = useState(false);
    const [schedules, setSchedules]       = useState([]);
    const [facilities, setFacilities]     = useState([]);
    const [reportFacility, setReportFac]  = useState(null);
    const [filters, setFilters]           = useState({
        facility_id: null,
        period_label: null,
        level: null,
        status: null,
    });

    // Auto-select current period
    useEffect(() => {
        if (currentPeriod && !filters.period_label) {
            setFilters(f => ({ ...f, period_label: currentPeriod.label }));
        }
    }, [currentPeriod]); // eslint-disable-line

    useEffect(() => {
        FacilityService.all({ status:1 }).then(r =>
            setFacilities(r.data?.result?.data || [])
        );
    }, []);

    // eslint-disable-next-line
    useEffect(() => { load(); }, [filters]);

useEffect(() => {
    trailPageView(TRAIL_MODULES.COMPLIANCE, window.location.pathname);
}, []); // eslint-disable-line
    const load = async () => {
        setLoading(true);
        try {
            const res = await QoCService.getAuditSchedules({
                ...(filters.facility_id  && { facility_id:  filters.facility_id  }),
                ...(filters.status       && { status:        filters.status       }),
            });
            let data = res.data?.data || [];
            // Client-side period filter by scheduled_date range
            if (filters.period_label) {
                const period = periods.find(p => p.label === filters.period_label);
                if (period) {
                    data = data.filter(s =>
                        moment(s.scheduled_date).isSameOrAfter(moment(period.period_start)) &&
                        moment(s.scheduled_date).isSameOrBefore(moment(period.period_end))
                    );
                }
            }
            if (filters.level) {
                data = data.filter(s => s.site_type === filters.level);
            }
            setSchedules(data);
        } catch {
            // silent
        } finally {
            setLoading(false);
        }
    };

    const today = moment().startOf('day');

    // ── Derived stats from schedules ──────────────────────────────────────────
    const total      = schedules.length;
    const completed  = schedules.filter(s => s.status === 'completed').length;
    const inProgress = schedules.filter(s => s.status === 'in_progress').length;
    const scheduled  = schedules.filter(s =>
        s.status === 'scheduled' && !moment(s.scheduled_date).isBefore(today)
    ).length;
    const overdue    = schedules.filter(s =>
        s.status !== 'completed' &&
        s.status !== 'cancelled' &&
        moment(s.scheduled_date).isBefore(today)
    );
    const cancelled  = schedules.filter(s => s.status === 'cancelled').length;
    const pctDone    = total > 0 ? Math.round(completed / total * 100) : 0;

    // Avg score of completed schedules that have a score
    const scored = schedules.filter(s => s.assessment_score && parseFloat(s.assessment_score) > 0);
    const avgScore = scored.length
        ? scored.reduce((sum, s) => sum + parseFloat(s.assessment_score), 0) / scored.length
        : null;

    // Group overdue by facility for summary
    const overdueByFac = overdue.reduce((acc, s) => {
        if (!acc[s.facility_id]) acc[s.facility_id] = {
            facility_id: s.facility_id,
            facility_name: s.facility_name,
            site_type: s.site_type,
            region: s.region,
            clauses: [],
            max_days: 0,
        };
        const days = today.diff(moment(s.scheduled_date), 'days');
        acc[s.facility_id].clauses.push({ code: s.clause_code, days, status: s.status });
        acc[s.facility_id].max_days = Math.max(acc[s.facility_id].max_days, days);
        return acc;
    }, {});
    const overdueGroups = Object.values(overdueByFac)
        .sort((a, b) => b.max_days - a.max_days);

    // Completed assessments with scores
    const completedWithScore = schedules
        .filter(s => s.status === 'completed' && s.assessment_score)
        .sort((a, b) => parseFloat(a.assessment_score) - parseFloat(b.assessment_score));

    const LEVELS = ['Clinic without Maternity','Clinic with Maternity','Public Health Unit','Regional Hospital','National Referral Hospital'];

    // ── Schedule detail table columns ─────────────────────────────────────────
    const schedColumns = [
        {
            title: 'Facility', dataIndex: 'facility_name', width: 200, ellipsis: true,
            render: (name, s) => (
                <div>
                    <div style={{ fontWeight:700, fontSize:12 }}>{name}</div>
                    <div style={{ fontSize:10, color:'#888' }}>{s.site_type} • {s.region}</div>
                </div>
            )
        },
        {
            title: 'Clause', width: 100,
            render: (_, s) => {
                const col = CLAUSE_COLORS[s.clause_code] || '#1890ff';
                return s.clause_code ? (
                    <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                        <div style={{ width:26, height:26, borderRadius:5, background:col,
                            color:'#fff', display:'flex', alignItems:'center',
                            justifyContent:'center', fontSize:9, fontWeight:800 }}>
                            {s.clause_code}
                        </div>
                        <span style={{ fontSize:10, color:'#555' }}>
                            {(s.clause_title||'').slice(0,14)}
                        </span>
                    </div>
                ) : <Tag>Full Audit</Tag>;
            }
        },
        {
            title: 'Scheduled Date', width: 120,
            render: (_, s) => {
                const isOD = s.status !== 'completed' && s.status !== 'cancelled'
                    && moment(s.scheduled_date).isBefore(today);
                const days = isOD ? today.diff(moment(s.scheduled_date), 'days') : 0;
                return (
                    <div>
                        <div style={{ fontWeight:600, fontSize:12,
                            color: isOD ? '#f5222d' : '#333' }}>
                            {moment(s.scheduled_date).format('DD MMM YYYY')}
                        </div>
                        {isOD && (
                            <Tag color="red" style={{ fontSize:9, marginTop:2 }}>
                                {days}d overdue
                            </Tag>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Lead Assessor', dataIndex: 'lead_assessor_name', width: 130, ellipsis: true,
            render: v => <span style={{ fontSize:12 }}>{v || <span style={{ color:'#ccc' }}>Unassigned</span>}</span>
        },
        {
            title: 'Status', width: 120,
            render: (_, s) => {
                const isOD = s.status !== 'completed' && s.status !== 'cancelled'
                    && moment(s.scheduled_date).isBefore(today);
                if (isOD) return <Tag color="red" style={{ fontWeight:700 }}>Overdue</Tag>;
                const cfg = {
                    completed:   { color:'green',  label:'Completed'   },
                    in_progress: { color:'orange', label:'In Progress'  },
                    scheduled:   { color:'blue',   label:'Scheduled'   },
                    cancelled:   { color:'default',label:'Cancelled'   },
                };
                const c = cfg[s.status] || cfg.scheduled;
                return <Tag color={c.color} style={{ fontWeight:700 }}>{c.label}</Tag>;
            }
        },
        {
            title: 'Score', width: 100,
            render: (_, s) => {
                if (!s.assessment_score || s.status !== 'completed') {
                    return <span style={{ color:'#ccc' }}>—</span>;
                }
                const pct = parseFloat(s.assessment_score);
                const r = getRating(pct);
                return (
                    <span style={{ fontWeight:900, fontSize:13, color:r.color }}>
                        {pct.toFixed(1)}%
                    </span>
                );
            }
        },
        {
            title: '', width: 80,
            render: (_, s) => (
                <Button size="small" type="link"
                    icon={<FilePdfOutlined style={{ color:'#f5222d' }}/>}
                     onClick={() => {
                        trailReportGenerate(TRAIL_MODULES.COMPLIANCE, 'Facility Report', { facilityName: s.facility_name });
                        setReportFac({
                            id: s.facility_id,
                            business_name: s.facility_name,
                            site_type: s.site_type,
                            located_region: s.region,
                        });
                    }}>
                    Report
                </Button>
            )
        },
    ];

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h2>Compliance Dashboard</h2>
                    <div style={{ fontSize:12, color:'#888', marginTop:2 }}>
                        Based on scheduled assessments — overdue = scheduled date passed, not completed
                    </div>
                </div>
                <div className="my-auto ml-auto">
                    <Button icon={<ReloadOutlined/>} onClick={load} loading={loading}>Refresh</Button>
                </div>
            </div>

            <div className="page-pad">

                {/* ── Filters ── */}
                <Card size="small" style={{ marginBottom:16 }}>
                    <Row gutter={12} align="middle">
                        <Col span={6}>
                            <label style={{ fontWeight:600, display:'block', marginBottom:3, fontSize:12 }}>
                                Assessment Period
                            </label>
                            <Select allowClear placeholder="All Periods" style={{ width:'100%' }}
                                value={filters.period_label || undefined}
                                onChange={v => setFilters(f => ({ ...f, period_label: v||null }))}>
                                {periods.map(p => (
                                    <Option key={p.label} value={p.label}>
                                        {parseInt(p.is_current) === 1 ? '★ ' : ''}{p.label}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={6}>
                            <label style={{ fontWeight:600, display:'block', marginBottom:3, fontSize:12 }}>
                                Facility
                            </label>
                            <Select allowClear showSearch placeholder="All Facilities"
                                optionFilterProp="children" style={{ width:'100%' }}
                                value={filters.facility_id || undefined}
                                onChange={v => setFilters(f => ({ ...f, facility_id: v||null }))}>
                                {facilities.map(f => (
                                    <Option key={f.id} value={f.id}>{f.business_name}</Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={4}>
                            <label style={{ fontWeight:600, display:'block', marginBottom:3, fontSize:12 }}>
                                Level
                            </label>
                            <Select allowClear placeholder="All Levels" style={{ width:'100%' }}
                                value={filters.level || undefined}
                                onChange={v => setFilters(f => ({ ...f, level: v||null }))}>
                                {LEVELS.map(l => <Option key={l} value={l}>{l}</Option>)}
                            </Select>
                        </Col>
                        <Col span={4}>
                            <label style={{ fontWeight:600, display:'block', marginBottom:3, fontSize:12 }}>
                                Status
                            </label>
                            <Select allowClear placeholder="All Statuses" style={{ width:'100%' }}
                                value={filters.status || undefined}
                                onChange={v => setFilters(f => ({ ...f, status: v||null }))}>
                                <Option value="scheduled">Scheduled</Option>
                                <Option value="in_progress">In Progress</Option>
                                <Option value="completed">Completed</Option>
                                <Option value="cancelled">Cancelled</Option>
                            </Select>
                        </Col>
                        <Col span={4} style={{ paddingTop:20 }}>
                            <Button onClick={() => setFilters({
                                facility_id:null, period_label:currentPeriod?.label||null,
                                level:null, status:null
                            })}>Clear</Button>
                        </Col>
                    </Row>
                </Card>

                {/* ── KPI cards ── */}
                <Spin spinning={loading}>
                    <Row gutter={[12,12]} style={{ marginBottom:16 }}>
                        {[
                            { label:'Total Scheduled',    val:total,      color:'#1890ff', icon:<CalendarOutlined/>   },
                            { label:'Completed',          val:completed,  color:'#52c41a', icon:<CheckCircleOutlined/>},
                            { label:'In Progress',        val:inProgress, color:'#faad14', icon:<ClockCircleOutlined/>},
                            { label:'Upcoming',           val:scheduled,  color:'#06b6d4', icon:<CalendarOutlined/>   },
                            { label:'Overdue',            val:overdue.length, color:'#f5222d', icon:<WarningOutlined/>},
                            { label:'Cancelled',          val:cancelled,  color:'#999',    icon:<ClockCircleOutlined/>},
                        ].map(k => (
                            <Col key={k.label} xs={12} sm={8} md={4}>
                                <div style={{ background:`${k.color}09`,
                                    border:`2px solid ${k.color}28`, borderRadius:10,
                                    padding:'14px 16px', textAlign:'center',
                                    cursor: k.label==='Overdue' ? 'pointer' : 'default' }}
                                    onClick={k.label==='Overdue'
                                        ? () => setFilters(f=>({...f, status:null}))
                                        : undefined}>
                                    <div style={{ fontSize:10, color:'#888', fontWeight:700,
                                        textTransform:'uppercase', letterSpacing:0.8, marginBottom:4 }}>
                                        {k.label}
                                    </div>
                                    <div style={{ fontSize:28, fontWeight:900, color:k.color }}>
                                        {k.val}
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>

                    {/* Progress bar */}
                    {total > 0 && (
                        <Card size="small" style={{ marginBottom:16 }}>
                            <Row gutter={24} align="middle">
                                <Col span={4}>
                                    <div style={{ textAlign:'center' }}>
                                        <div style={{ fontSize:10, color:'#888', fontWeight:700, marginBottom:4 }}>
                                            COMPLETION
                                        </div>
                                        <Progress type="circle" percent={pctDone} width={72}
                                            strokeColor={pctDone===100?'#52c41a':pctDone>60?'#1890ff':'#fa8c16'}
                                            format={v=><span style={{ fontSize:14, fontWeight:900 }}>{v}%</span>}/>
                                    </div>
                                </Col>
                                <Col span={14}>
                                    <div style={{ fontSize:12, fontWeight:600, color:'#555', marginBottom:6 }}>
                                        Schedule Progress — {completed} of {total} clauses completed
                                    </div>
                                    <div style={{ display:'flex', gap:2, height:18, borderRadius:9, overflow:'hidden' }}>
                                        {completed  > 0 && <div style={{ flex:completed,  background:'#52c41a' }}/>}
                                        {inProgress > 0 && <div style={{ flex:inProgress, background:'#faad14' }}/>}
                                        {overdue.length > 0 && <div style={{ flex:overdue.length, background:'#f5222d' }}/>}
                                        {scheduled  > 0 && <div style={{ flex:scheduled,  background:'#1890ff' }}/>}
                                        {cancelled  > 0 && <div style={{ flex:cancelled,  background:'#e0e0e0' }}/>}
                                    </div>
                                    <div style={{ display:'flex', gap:14, marginTop:6, fontSize:10, color:'#888' }}>
                                        <span style={{ color:'#52c41a' }}>■ Completed {completed}</span>
                                        <span style={{ color:'#faad14' }}>■ In Progress {inProgress}</span>
                                        <span style={{ color:'#f5222d' }}>■ Overdue {overdue.length}</span>
                                        <span style={{ color:'#1890ff' }}>■ Scheduled {scheduled}</span>
                                        {cancelled > 0 && <span style={{ color:'#bbb' }}>■ Cancelled {cancelled}</span>}
                                    </div>
                                </Col>
                                {avgScore !== null && (
                                    <Col span={6}>
                                        <div style={{ textAlign:'center' }}>
                                            <div style={{ fontSize:10, color:'#888', fontWeight:700, marginBottom:4 }}>
                                                AVG SCORE (COMPLETED)
                                            </div>
                                            <div style={{ fontSize:32, fontWeight:900,
                                                color: getRating(avgScore).color }}>
                                                {avgScore.toFixed(1)}%
                                            </div>
                                            <div style={{ fontSize:11, color: getRating(avgScore).color, fontWeight:700 }}>
                                                {getRating(avgScore).label}
                                            </div>
                                        </div>
                                    </Col>
                                )}
                            </Row>
                        </Card>
                    )}

                    {/* ── Overdue section ── */}
                    {overdue.length > 0 && (
                        <Card
                            size="small"
                            style={{ marginBottom:16, border:'2px solid #ffccc7' }}
                            title={
                                <span style={{ color:'#f5222d', fontWeight:800 }}>
                                    <WarningOutlined style={{ marginRight:6 }}/>
                                    Overdue Schedules ({overdue.length})
                                    <span style={{ fontSize:11, fontWeight:400, color:'#888', marginLeft:8 }}>
                                        — scheduled_date has passed and assessment not completed
                                    </span>
                                </span>
                            }>
                            {overdueGroups.length > 0 && (
                                <div style={{ marginBottom:12 }}>
                                    {overdueGroups.slice(0, 5).map(fac => (
                                        <div key={fac.facility_id} style={{
                                            display:'flex', alignItems:'center', gap:10,
                                            padding:'6px 10px', marginBottom:4,
                                            background:'#fff2f0', borderRadius:6,
                                            border:'1px solid #ffccc7' }}>
                                            <div style={{ flex:1 }}>
                                                <span style={{ fontWeight:700, fontSize:12 }}>{fac.facility_name}</span>
                                                <span style={{ fontSize:10, color:'#888', marginLeft:8 }}>
                                                    {fac.site_type}
                                                </span>
                                            </div>
                                            <div style={{ display:'flex', gap:4 }}>
                                                {fac.clauses.map((cl, i) => (
                                                    <Tag key={i}
                                                        style={{ background: CLAUSE_COLORS[cl.code]||'#f5222d',
                                                            color:'#fff', border:'none', fontSize:9, fontWeight:700 }}>
                                                        {cl.code || 'FULL'}
                                                    </Tag>
                                                ))}
                                            </div>
                                            <Tag color="red" style={{ fontSize:10, fontWeight:700 }}>
                                                {fac.max_days}d overdue
                                            </Tag>
                                            <Button size="small" danger ghost
                                                 onClick={() => {
                                    trailReportGenerate(TRAIL_MODULES.COMPLIANCE, 'Facility Report', { facilityName: fac.facility_name });
                                    setReportFac({
                                        id: fac.facility_id,
                                        business_name: fac.facility_name,
                                        site_type: fac.site_type,
                                    });
                                }}>
                                                Report
                                            </Button>
                                        </div>
                                    ))}
                                    {overdueGroups.length > 5 && (
                                        <div style={{ fontSize:11, color:'#888', padding:'4px 10px' }}>
                                            + {overdueGroups.length - 5} more overdue facilities
                                        </div>
                                    )}
                                </div>
                            )}
                        </Card>
                    )}

                    {/* Alert if no schedules at all */}
                    {!loading && total === 0 && (
                        <Alert
                            message="No schedules found for the selected filters"
                            description="Create audit schedules in the Audit Schedules module to track compliance progress here."
                            type="info" showIcon style={{ marginBottom:16 }}/>
                    )}

                    {/* ── Full schedule table ── */}
                    <Card size="small" title={`All Schedules (${total})`}>
                        <Table
                            columns={schedColumns}
                            dataSource={schedules}
                            rowKey="id"
                            loading={loading}
                            size="small"
                            pagination={{ pageSize:20, showTotal: t => `${t} schedules` }}
                            rowClassName={s => {
                                const isOD = s.status !== 'completed' && s.status !== 'cancelled'
                                    && moment(s.scheduled_date).isBefore(today);
                                return isOD ? 'ant-table-row-overdue' : '';
                            }}/>
                    </Card>

                    {/* ── Low-scoring completed assessments ── */}
                    {completedWithScore.filter(s => parseFloat(s.assessment_score) < 70).length > 0 && (
                        <Card size="small"
                            style={{ marginTop:16, border:'2px solid #fff7e6' }}
                            title={
                                <span style={{ color:'#fa8c16', fontWeight:800 }}>
                                    ⚠ Requires Attention — Completed but scored below 70%
                                </span>
                            }>
                            <Table
                                size="small"
                                dataSource={completedWithScore.filter(s => parseFloat(s.assessment_score) < 70)}
                                rowKey="id"
                                pagination={{ pageSize:10, size:'small' }}
                                columns={[
                                    { title:'Facility', dataIndex:'facility_name', width:200, ellipsis:true },
                                    { title:'Clause', width:80, render:(_,s) => {
                                        const col = CLAUSE_COLORS[s.clause_code]||'#1890ff';
                                        return s.clause_code
                                            ? <Tag style={{ background:col, color:'#fff', border:'none', fontSize:10 }}>{s.clause_code}</Tag>
                                            : <Tag>Full</Tag>;
                                    }},
                                    { title:'Audit Date', dataIndex:'scheduled_date', width:110,
                                        render:d=>moment(d).format('DD MMM YYYY') },
                                    { title:'Score', width:90, render:(_,s) => {
                                        const r = getRating(parseFloat(s.assessment_score||0));
                                        return <span style={{ fontWeight:900, color:r.color, fontSize:13 }}>
                                            {parseFloat(s.assessment_score).toFixed(1)}%
                                        </span>;
                                    }},
                                    { title:'Rating', width:160, render:(_,s) => {
                                        const r = getRating(parseFloat(s.assessment_score||0));
                                        return <Tag style={{ background:r.color, color:'#fff', border:'none', fontWeight:700 }}>{r.label}</Tag>;
                                    }},
                                    { title:'', width:90, render:(_,s) => (
                                        <Button size="small" type="link" icon={<FilePdfOutlined style={{ color:'#f5222d' }}/>}
                                            onClick={() => {
                                trailReportGenerate(TRAIL_MODULES.COMPLIANCE, 'Facility Report', { facilityName: s.facility_name });
                                setReportFac({
                                    id:s.facility_id, business_name:s.facility_name,
                                    site_type:s.site_type
                                });
                            }}>
                                            Report
                                        </Button>
                                    )},
                                ]}/>
                        </Card>
                    )}
                </Spin>
            </div>

            {reportFacility && (
                <FacilityReportModal
                    facilityProp={reportFacility}
                    onClose={() => setReportFac(null)}/>
            )}
        </div>
    );
}
